import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { useFieldArray, useForm } from "react-hook-form";
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import "react-datepicker/dist/react-datepicker.css";
import BeginningBalanceImportModal from '../../../components/Modal/BeginningBalanceImport/BeginningBalanceImportModal';
import { QBBalanceSheetPLListFormsI } from '../../../resources/form-props';
import { QuickBookBSPLValidate } from '../../../resources/form-validator';
import { useThirdPartyApi } from '../../../resources/hooks/api/thirdPartyApiHook';
import { useTransTableDataApi } from '../../../resources/hooks/api/transApiHook';
import { getThirdPartyStepsCompleted } from '../../../store/settings/selectors';
import { getBranch } from '../../../store/user/selectors';

// import '../GlClassification/GlClassification.css'
import { URLS } from '../../../resources/constants';
import { calculateMaxHeightContent } from '../../../resources/functions';
import { useChartAccountApi } from '../../../resources/hooks/api/chartAccountApiHook';
import { useSettingsApi } from '../../../resources/hooks/api/useSettingsApiHook';

interface Props {

}

const QBBalanceSheetPLList: React.FC<Props> = () => {

    const { qbPostBSPL, qbFetchBSPL, quickBookImportData, qbFetchJobStatus, qbFetchImportJobStatus, qbFetchLedgerAudit, qbRetryLedgerPeriod, qbRetryLedgerPeriods, qbSaveLedgerMappings } = useThirdPartyApi();
    const { createTransactionAdjustment } = useTransTableDataApi()
    const { glAccountsList } = useChartAccountApi();
    const { fetchAccountSettingsData } = useSettingsApi();
    const quickBookStepCompleted = useSelector(getThirdPartyStepsCompleted);

    const navigate = useNavigate();
    const branchData = useSelector(getBranch)
    
    const { t } = useTranslation("scenes.thirdparty.quickbook.QBBalanceSheetPLList");

    const [records, setRecords] = useState<any>({
        data: [],
        total_records: 0,
    });
    const [recordsUpdateKey, setRecordsUpdateKey] = useState<number>(0);
    const [searchParams, setSearchParams] = useState<any>({
        page: 1,
        per_page: 80
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
    const [isOpenBBModal, setIsOpenBBModal] = useState<boolean>(false);
    const checkAllRef = useRef<HTMLInputElement>(null);
    const [selectedDateBB, setSelectedDateBB] = useState<Date[]>([]);
    const [totalRemovedRecord, setTotalRemovedRecord] = useState<number>(0);
    const [submitJobId, setSubmitJobId] = useState<string | null>(null);
    const [submitJobStatus, setSubmitJobStatus] = useState<'idle' | 'in_progress' | 'completed' | 'failed'>('idle');
    const [submitJobDescription, setSubmitJobDescription] = useState<string>('');
    const [submitJobError, setSubmitJobError] = useState<string>('');
    const [verificationOverride, setVerificationOverride] = useState<{ records: any; removeRecords: number[] } | null>(null);
    const [lastSubmittedRecords, setLastSubmittedRecords] = useState<any>(null);
    const [lastRemovedRecords, setLastRemovedRecords] = useState<number[]>([]);
    const [importInProgress, setImportInProgress] = useState<boolean>(false);
    const [importJobId, setImportJobId] = useState<string | null>(localStorage.getItem("qbo_import_job_id"));
    const [importJobStatus, setImportJobStatus] = useState<string>('idle');
    const [importJobDescription, setImportJobDescription] = useState<string>('');
    const [failedImportMonths, setFailedImportMonths] = useState<string[]>([]);
    const [ledgerAudit, setLedgerAudit] = useState<any>(null);
    const [ledgerAuditLoading, setLedgerAuditLoading] = useState<boolean>(false);
    const [ledgerRetrying, setLedgerRetrying] = useState<boolean>(false);
    const [retryingAll, setRetryingAll] = useState<boolean>(false);
    const [ledgerMappingSaving, setLedgerMappingSaving] = useState<boolean>(false);
    const [ledgerOverrideEdits, setLedgerOverrideEdits] = useState<any>({});
    const [registerOverrideEdits, setRegisterOverrideEdits] = useState<any>({});
    const [glAccountOptions, setGlAccountOptions] = useState<any[]>([]);
    const [showLedgerDetails, setShowLedgerDetails] = useState<boolean>(false);
    const [showDashboardNavConfirmModal, setShowDashboardNavConfirmModal] = useState<boolean>(false);

    const handleBackToDashboard = () => {
        if (isLoading || (submitJobStatus === 'in_progress') || importInProgress) {
            setShowDashboardNavConfirmModal(true);
        } else {
            navigate(URLS.QUICKBOOK_DASHBOARD);
        }
    };
    const { register, control, trigger, handleSubmit, reset, setValue, getValues, watch, formState: { errors } } = useForm<QBBalanceSheetPLListFormsI>({
        defaultValues: {
            BS_PL: []
        },
        resolver: yupResolver(QuickBookBSPLValidate)
    });
    const { fields, append: formAppend, remove: formRemove } = useFieldArray({
        control,
        name: "BS_PL"
    });
    const watchedPeriods = watch("BS_PL");

    const parseFailedMonths = (message: string = '') => (
        message.match(/\b\d{2}\/\d{4}\b/g) || []
    );

    const parseImportTotalMonths = (message: string = '') => {
        const match = message.match(/\bof\s+(\d+)\b/) || message.match(/\b(\d+)\s+months?\b/);
        return match ? Number(match[1]) : 0;
    }

    const trackImportJob = (jobId?: string) => {
        if (!jobId) return;
        localStorage.setItem("qbo_import_job_id", jobId);
        setImportJobId(jobId);
        setImportJobStatus('in_progress');
        setFailedImportMonths([]);
    };

    const applyImportJobStatus = (data: any) => {
        if (!data) return;
        const failedMonths = parseFailedMonths(data.failure_message || '');
        setImportJobId(data.job_id || null);
        setImportJobDescription(data.description || '');
        setImportJobStatus(data.status || 'unknown');
        setFailedImportMonths(failedMonths);

        if (data.status === 'completed') {
            setImportInProgress(false);
            localStorage.removeItem("qbo_import_job_id");
            localStorage.removeItem("qbo_duration");
        } else if ((data.status === 'failed') || (data.status === 'partial_failed')) {
            setImportInProgress(false);
        } else {
            setImportInProgress(true);
            if (data.job_id) localStorage.setItem("qbo_import_job_id", data.job_id);
        }
    };

    useEffect(() => {
        // Use reset to completely replace form data - this is synchronous and reliable
        reset({
            BS_PL: records.data.map((record: any, index: number) => ({
                formIndex: index,
                checked: false,
                field_id: record['id'],
                month: record['month'],
                year: record['year'],
                status: record['status'],
                is_submited: record['is_submited'],
                ledger_review_needed: record['ledger_review_needed'],
                ledger_blocker_count: record['ledger_blocker_count'],
                verification_review_needed: record['verification_review_needed'],
                verification_blocker_count: record['verification_blocker_count'],
                verification_status: record['verification_status'],
            }))
        });
    }, [recordsUpdateKey])

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(131));
    },[])

    useEffect(() => {
        if (fields.length == 0) {
            if (checkAllRef.current) {
                checkAllRef.current.checked = false;
            }
        }
        // if (fields.length == records.data.length) {
        //     if (checkAllRef.current) {
        //         checkAllRef.current.checked = true;
        //     }
        // }
    }, [fields])

    useEffect(() => {
        // Poll if there are pending records (status == 1) or an import was just kicked off
        const hasPendingRecords = records.data.some((record: any) => record.status === 1);

        if (hasPendingRecords || importInProgress) {
            const interval = setInterval(() => {
                fetchTransactionList(false);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [searchParams.page, records.data, importInProgress])


    const handleCheckAllChange = (e: any) => {
        if (checkAllRef.current) {
            checkAllRef.current.checked = e.target.checked;

            records.data.forEach((record: any, index: number) => {
                if (isSelectablePeriod(record)) {
                    setValue(`BS_PL.${index}.checked`, e.target.checked)
                }
            })
        }
    };

    const fetchTransactionList = (reload = true) => {
        if (!branchData?.id) return;

        const params: any = {
            branch_id: +branchData['id'],
            "page": searchParams['page'],
            "per_page": searchParams['per_page'],
        }

        // reset({
        //     BS_PL: []
        // })

        // setIsLoading(true);
        qbFetchBSPL(params, (message: string, resp: any) => {
            const newData = resp.data.data.result;
            const latestImportJob = resp.data.data.latest_import_job;
            
            setRecords({
                data: newData,
                total_records: resp.data.data.total,
            });
            setRecordsUpdateKey(prev => prev + 1); // Force update
            if (latestImportJob && (
                (latestImportJob.status !== 'completed') ||
                importInProgress ||
                !!localStorage.getItem("qbo_duration")
            )) {
                applyImportJobStatus(latestImportJob);
            }
            
            // Check if all records are completed
            const totalExpected = parseInt(localStorage.getItem("qbo_duration") || "0");
            const allCompleted = (newData.length > 0) && (newData.length >= totalExpected) && newData.every((record: any) => record.status === 2);

            if (allCompleted && importInProgress) {
                setImportInProgress(false);
                localStorage.removeItem("qbo_duration");
            }
            if ((newData.length === 0) && !importInProgress) {
                localStorage.removeItem("qbo_duration");
            }

        }, (message: string, resp: any) => {
            // setIsLoading(false);
            toast.error(message);
        })
    }

    const handleCheckAll = (e: any, index: number) => {
        // setValue(`BS_PL.${index}.checked`, e.target.checked);

        let recordPos = index;
        if (e.target.checked) {
            do {
                setValue(`BS_PL.${recordPos}.checked`, true);
                recordPos = recordPos - 1;
            } while (recordPos >= 0);
        } else {
            while (recordPos <= (records.data.length - 1)) {
                setValue(`BS_PL.${recordPos}.checked`, false);
                recordPos = recordPos + 1;
            }
        }


        if (e.target.checked) {
            const checkedRecords = records.data.filter((record: any, index: number) => getValues(`BS_PL.${index}.checked`) === true);

            if (checkedRecords.length == records.data.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = true;
                }
            }
        } else {
            const uncheckedRecords = records.data.filter((record: any, index: number) => getValues(`BS_PL.${index}.checked`) === false);

            if (uncheckedRecords.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = false;
                }
            }
        }
    }

    const processData = (data: any) => {
        const removeRecords: number[] = [];
        const selectedRecords = data['BS_PL']
            .map((d: any, index: number) => {
                if (d['checked'] && (d["status"] == 2) && !recordHasBlockers(d)) {
                    removeRecords.push(index);
                    return {
                        // id: d.id,
                        month: d.month,
                        year: d.year
                    };
                } else {
                    return null;
                }
            })
            .filter((e: any) => e); // Filter out null values
        if (selectedRecords.length > 0) {
            postData(selectedRecords, removeRecords);
        } else {
            toast.error(t("table.actions.submit"));
            return;
        }

    };
    const postData = (selectedRecords: any, removeRecords: number[], overrideVerification: boolean = false) => {
        setIsLoading(true);
        setSubmitJobStatus('idle');
        setSubmitJobError('');
        qbPostBSPL({ records: selectedRecords, branch_id: +branchData['id'], override_verification: overrideVerification }, (message: string, resp: any) => {
            const data = resp.data.data;
            setVerificationOverride(null);
            if (data && data.job_id) {
                // Async mode — poll for completion
                setSubmitJobId(data.job_id);
                setSubmitJobStatus('in_progress');
                setSubmitJobDescription(t('alert.submitting.queued', { count: data.total_months || selectedRecords.length }));
                setLastSubmittedRecords(selectedRecords);
                setLastRemovedRecords(removeRecords);
                toast.success(t('toast.submissionStarted'));
            } else {
                // Legacy sync mode fallback
                setIsLoading(false);
                formRemove(removeRecords);
                setTotalRemovedRecord(totalRemovedRecord + removeRecords.length);
                localStorage.removeItem("qbo_duration");
                toast.success(message);
                navigate(URLS.QUICKBOOK_DASHBOARD);
            }
        }, (message: string, resp: any) => {
            setIsLoading(false);
            // BS/PL didn't balance vs QBO. Offer an acknowledged override instead of a dead end.
            const blocked = /import_verification_blocked/i.test(JSON.stringify(resp || '')) || /verification/i.test(message || '');
            if (blocked && !overrideVerification) {
                setVerificationOverride({ records: selectedRecords, removeRecords });
            }
            toast.error(message);
        })
    }

    const submitAnyway = () => {
        if (!verificationOverride) return;
        postData(verificationOverride.records, verificationOverride.removeRecords, true);
    }

    const retrySubmit = () => {
        if (lastSubmittedRecords && (lastRemovedRecords.length > 0)) {
            postData(lastSubmittedRecords, lastRemovedRecords);
        }
    }

    const formatMoney = (value: any) => {
        return Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    const hasLedgerBlockers = (audit: any) => {
        const lineCounts = audit?.status_counts || {};
        const transactionCounts = audit?.transaction_status_counts || {};
        return (
            (Number(lineCounts.invalid || 0) > 0) ||
            (Number(lineCounts.unmapped || 0) > 0) ||
            (Number(transactionCounts.blocked || 0) > 0) ||
            (Number(transactionCounts.incomplete || 0) > 0) ||
            (Number(transactionCounts.unbalanced || 0) > 0)
        );
    }

    const hasVerificationBlockers = (audit: any) => Number(audit?.verification?.blocker_count || 0) > 0;

    const formatLedgerStatus = (status: string) => t(`ledgerStatus.${status}`, { defaultValue: String(status || '').replace(/_/g, ' ') });

    const areaLabel = (area: string) => t(`ledgerAudit.area${area}`, { defaultValue: area });

    const glAccountLabel = (accountId: any) => {
        const account = glAccountOptions.find((option: any) => Number(option.value) === Number(accountId));
        return account?.label || accountId || t('ledgerAudit.notMapped');
    }

    const verificationAccountLabel = (row: any) => (
        row?.revrex_account_name
            ? `${row.revrex_account_name}${row.revrex_account_number ? ` (${row.revrex_account_number})` : ''}`
            : glAccountLabel(row?.revrex_account_id)
    );

    const verificationAccountOption = (row: any) => {
        const label = verificationAccountLabel(row);
        const matchedAccount = glAccountOptions.find((account: any) => account.label === label);
        return {
            value: matchedAccount?.value || row.revrex_account_id,
            label,
        };
    }

    const ledgerStatusKeys = (audit: any) => Array.from(new Set([
        ...Object.keys(audit?.status_counts || {}),
        ...Object.keys(audit?.transaction_status_counts || {}),
    ]));

    const fetchGlAccountOptions = () => {
        if (!branchData?.id || (glAccountOptions.length > 0)) return;
        glAccountsList({
            branch_id: +branchData['id'],
            page: 1,
            per_page: 5000,
        }, (_message: string, resp: any) => {
            const mappedOptions = (resp?.data?.data?.result || [])
                .map((account: any) => {
                    const mappedAccount = account.account_chart || {};
                    const mappedId = account.gl_mapping_account_id || mappedAccount.id || account.id;
                    const accountName = mappedAccount.name || account.name || account.NAME || '';
                    const accountNumber = mappedAccount.account || account.account || '';
                    return mappedId ? {
                        value: +mappedId,
                        label: `${accountName}${accountNumber ? ` (${accountNumber})` : ''}`,
                    } : null;
                })
                .filter(Boolean);
            setGlAccountOptions(mappedOptions.filter((account: any, index: number, all: any[]) => (
                all.findIndex((candidate: any) => Number(candidate.value) === Number(account.value)) === index
            )));
        }, (message: string) => {
            toast.error(message);
        });
    }

    const fetchLedgerAudit = (record: any) => {
        if (!branchData?.id) return;
        setLedgerAuditLoading(true);
        qbFetchLedgerAudit({
            branch_id: +branchData['id'],
            month: Number(record.month),
            year: Number(record.year),
            differences_only: true,
            limit: 25,
            comparison_limit: 25,
        }, (_message: string, resp: any) => {
            const audit = resp?.data?.data || null;
            setLedgerAudit(audit);
            setLedgerOverrideEdits({});
            setRegisterOverrideEdits((audit?.register_mapping_overrides || []).reduce((acc: any, row: any) => ({
                ...acc,
                [registerOverrideKey(row.qbo_transaction_id, row.from_revrex_account_id)]: Number(row.to_revrex_account_id),
            }), {}));
            if (((audit?.exceptions?.items || []).length > 0) || (Number(audit?.verification?.blocker_count || 0) > 0)) {
                fetchGlAccountOptions();
            }
            setLedgerAuditLoading(false);
        }, (message: string) => {
            setLedgerAuditLoading(false);
            toast.error(message);
        });
    }

    const handleLedgerOverrideChange = (lineId: any, value: string) => {
        setLedgerOverrideEdits((prev: any) => ({
            ...prev,
            [lineId]: value ? Number(value) : null,
        }));
    }

    const registerTxnId = (row: any) => {
        if (row?.qbo_transaction_id) return String(row.qbo_transaction_id);
        const match = String(row?.message || '').match(/Register transaction\s+([^\s]+)/i);
        return match?.[1] || '';
    }

    const registerOverrideKey = (txnId: any, fromAccountId: any) => `${txnId || ''}::${fromAccountId || ''}`;

    const registerMoveCandidates = (row: any) => (ledgerAudit?.verification?.details || []).filter((candidate: any) => (
        (candidate.area === 'REGISTER')
        && (registerTxnId(candidate) === registerTxnId(row))
        && (Number(candidate.qbo_total || 0) > 0)
        && (Number(candidate.revrex_total || 0) === 0)
    ));

    const canMoveRegisterRow = (row: any) => (row.area === 'REGISTER')
        && registerTxnId(row)
        && !String(row.message || '').includes('missing from QBO entity detail')
        && (Number(row.revrex_total || 0) > 0)
        && (Number(row.qbo_total || 0) === 0);

    const handleRegisterOverrideChange = (row: any, value: string) => {
        const key = registerOverrideKey(registerTxnId(row), row.revrex_account_id);
        setRegisterOverrideEdits((prev: any) => ({
            ...prev,
            [key]: value ? Number(value) : null,
        }));
    }

    const ledgerOverridePayload = () => {
        const lines = (ledgerAudit.exceptions?.items || [])
            .filter((row: any) => Object.prototype.hasOwnProperty.call(ledgerOverrideEdits, row.id))
            .map((row: any) => ({
                id: row.id,
                override_gl_mapping_account_id: ledgerOverrideEdits[row.id],
            }));
        const register_overrides = (ledgerAudit.verification?.details || [])
            .filter((row: any) => canMoveRegisterRow(row))
            .map((row: any) => {
                const qbo_transaction_id = registerTxnId(row);
                const from_revrex_account_id = row.revrex_account_id;
                const to_revrex_account_id = registerOverrideEdits[registerOverrideKey(qbo_transaction_id, from_revrex_account_id)];
                if (!to_revrex_account_id) return null;
                return {
                    month: Number(ledgerAudit.period.month),
                    year: Number(ledgerAudit.period.year),
                    qbo_transaction_id,
                    from_revrex_account_id: Number(from_revrex_account_id),
                    to_revrex_account_id,
                };
            })
            .filter(Boolean);
        return { lines, register_overrides };
    }

    const ledgerOverrideError = (message: string, data?: any) => {
        const detail = data?.error ? ` ${JSON.stringify(data.error)}` : '';
        return `${message || t('ledgerAudit.saveOverridesFailed')}${detail}`;
    }

    const saveLedgerOverrides = () => {
        if (!branchData?.id || !ledgerAudit) return;
        const { lines, register_overrides } = ledgerOverridePayload();

        if ((lines.length === 0) && (register_overrides.length === 0)) {
            toast.error(t('ledgerAudit.noOverrideChanges'));
            return;
        }

        setLedgerMappingSaving(true);
        qbSaveLedgerMappings({
            branch_id: +branchData['id'],
            lines,
            register_overrides,
        }, (message: string) => {
            setLedgerMappingSaving(false);
            toast.success(message || t('ledgerAudit.overridesSaved'));
            fetchLedgerAudit(ledgerAudit.period);
        }, (message: string, data: any) => {
            setLedgerMappingSaving(false);
            toast.error(ledgerOverrideError(message, data));
        });
    }

    const runLedgerRetry = () => {
        if (!branchData?.id || !ledgerAudit?.period) return;
        setLedgerRetrying(true);
        qbRetryLedgerPeriod({
            branch_id: +branchData['id'],
            month: Number(ledgerAudit.period.month),
            year: Number(ledgerAudit.period.year),
        }, (message: string, resp: any) => {
            setLedgerRetrying(false);
            setImportInProgress(true);
            localStorage.setItem("qbo_duration", "1");
            toast.success(t('ledgerAudit.retryStarted'));
            fetchTransactionList(false);
        }, (message: string) => {
            setLedgerRetrying(false);
            toast.error(message);
        });
    }

    const retryLedgerPeriod = () => {
        if (!branchData?.id || !ledgerAudit?.period) return;
        const { lines, register_overrides } = ledgerOverridePayload();
        if ((lines.length === 0) && (register_overrides.length === 0)) {
            runLedgerRetry();
            return;
        }
        setLedgerMappingSaving(true);
        qbSaveLedgerMappings({
            branch_id: +branchData['id'],
            lines,
            register_overrides,
        }, () => {
            setLedgerMappingSaving(false);
            setLedgerOverrideEdits({});
            setRegisterOverrideEdits({});
            runLedgerRetry();
        }, (message: string, data: any) => {
            setLedgerMappingSaving(false);
            toast.error(ledgerOverrideError(message, data));
        });
    }

    const periodsNeedingReview = () => fields.filter((f: any) => (Number(f.status) === 2) && recordHasBlockers(f));

    const periodNeedsReimport = (record: any) => Number(record.status) === 3;

    const isSelectablePeriod = (record: any) => (
        !(record?.duplicate || record?.posted) && (
            periodNeedsReimport(record) ||
            ((Number(record?.status) === 2) && !recordHasBlockers(record))
        )
    );

    const selectedPeriodsNeedingReview = () => {
        watchedPeriods; // keep the button label in sync with checkbox changes
        return fields.filter((f: any, index: number) => (
            (getValues(`BS_PL.${index}.checked`) === true) && (Number(f.status) === 2) && recordHasBlockers(f)
        ));
    };

    const periodsNeedingReimport = () => fields.filter((f: any) => periodNeedsReimport(f));

    const selectedPeriodsNeedingReimport = () => {
        watchedPeriods; // keep the button label in sync with checkbox changes
        return fields.filter((f: any, index: number) => (
            (getValues(`BS_PL.${index}.checked`) === true) && periodNeedsReimport(f)
        ));
    };

    const retryAllPeriods = () => {
        if (!branchData?.id) return;
        const selectedPeriods = selectedPeriodsNeedingReview();
        const periods = (selectedPeriods.length > 0 ? selectedPeriods : periodsNeedingReview())
            .map((f: any) => ({ month: Number(f.month), year: Number(f.year) }));
        if (periods.length === 0) {
            toast.error(t('ledgerAudit.noPeriodsToRetry'));
            return;
        }
        setRetryingAll(true);
        qbRetryLedgerPeriods({ branch_id: +branchData['id'], periods }, () => {
            setRetryingAll(false);
            setLedgerAudit(null);
            setImportInProgress(true);
            localStorage.setItem("qbo_duration", String(periods.length));
            toast.success(t('ledgerAudit.retryStarted'));
            fetchTransactionList(false);
        }, (message: string) => {
            setRetryingAll(false);
            toast.error(message);
        });
    }

    const retrySelectedOrCurrentPeriod = () => {
        const selectedPeriods = selectedPeriodsNeedingReview();
        if (selectedPeriods.length > 0) {
            retryAllPeriods();
            return;
        }
        retryLedgerPeriod();
    }

    const isLedgerAuditRecord = (record: any) => (
        ledgerAudit?.period &&
        (Number(record.month) === Number(ledgerAudit.period.month)) &&
        (Number(record.year) === Number(ledgerAudit.period.year))
    );

    const rowClassName = (record: any) => {
        if (isLedgerAuditRecord(record)) return 'table-info';
        if (periodNeedsReimport(record)) return 'table-danger';
        if (verificationBlocked(record)) return 'table-danger';
        if (ledgerBlocked(record)) return 'table-warning';
        return '';
    }

    const ledgerBlocked = (record: any) => (Number(record.ledger_blocker_count || 0) > 0) || (
        (record.ledger_blocker_count === undefined) && !!record.ledger_review_needed
    );

    const verificationBlocked = (record: any) => (Number(record.verification_blocker_count || 0) > 0) || (
        (record.verification_blocker_count === undefined) && !!record.verification_review_needed
    );

    const recordHasBlockers = (record: any) => ledgerBlocked(record) || verificationBlocked(record);

    const onChangePage = (event: any, page: number) => {
        reset({
            BS_PL: []
        })
        setSearchParams((prevState: any) => ({
            ...prevState,
            page: page
        }))
    }

    const getSettingsData = () => {
        setIsLoading(true)
        fetchAccountSettingsData({ branch_id: +branchData['id'] }, (msg: string, resp: any) => {
            setIsLoading(false)
            toast.success(t("table.noDataLeft"));
            navigate(URLS.DASHBOARD);
        }, (msg: string, resp: any) => {
            setIsLoading(false)
        })
    }

    const _handleQuickBookImportData = (step: number, datesOverride?: Date[]) => {
        const dates = datesOverride && (datesOverride.length > 0) ? datesOverride : selectedDateBB;

        if (!dates || !dates.length || !dates[0] || !dates[1]) {
            setIsOpenBBModal(true);
            return;
        }

        const params: any = {};
        params['branch_id'] = +branchData['id'];
        params['step'] = step;

        params['bs_import_start_date'] = moment(dates[0]).startOf('month').format('YYYY-MM-DD');
        params['bs_import_end_date'] = moment(dates[1]).format('YYYY-MM-DD');

        setIsLoading(true)
        quickBookImportData(params, (message: string, resp: any) => {

            // const d = localStorage.getItem("qbo_duration") || 0;
            localStorage.setItem("qbo_duration", moment(params['bs_import_end_date']).diff(moment(params['bs_import_start_date']), 'months') + 1 +  "");
            trackImportJob(resp?.data?.data?.job_id);

            setIsLoading(false);
            setSelectedDateBB([])
            setImportInProgress(true);
            toast.success(message);
            fetchTransactionList(false);
        }, (message: string, resp: any) => {
            setIsLoading(false)
            setSelectedDateBB([])
            toast.error(message)
        })
    }

    const _handleBBDateSelection = (dates?: Date[]) => {
        _handleQuickBookImportData(quickBookStepCompleted === 3 ? quickBookStepCompleted: 3, dates);
    }

    useEffect(() => {
        fetchTransactionList(false)
    }, [searchParams['page'], branchData?.id])

    // Detect in-progress import on initial load
    useEffect(() => {
        const hasPendingRecords = records.data.some((record: any) => record.status === 1);
        if (hasPendingRecords) {
            setImportInProgress(true);
        }
    }, [records.data.length])

    const refreshImportJobStatus = () => {
        if (!importJobId || !branchData?.id) return;
        qbFetchImportJobStatus({ job_id: importJobId, branch_id: +branchData['id'] }, (message: string, resp: any) => {
            applyImportJobStatus(resp.data.data);
        }, () => {
            // Period polling still runs; keep job status polling quiet on transient errors.
        });
    }

    useEffect(() => {
        if (!importJobId || !branchData?.id || ['completed', 'failed', 'partial_failed'].includes(importJobStatus)) return;
        refreshImportJobStatus();
        const interval = setInterval(refreshImportJobStatus, 5000);
        return () => clearInterval(interval);
    }, [importJobId, branchData?.id, importJobStatus])

    const retryFailedImportMonths = () => {
        if (!failedImportMonths.length || !branchData?.id) return;
        const dates = failedImportMonths.map((period: string) => moment(period, 'MM/YYYY'));
        const startDate = moment.min(dates).startOf('month');
        const endDate = moment.max(dates).endOf('month');
        const params = {
            branch_id: +branchData['id'],
            step: quickBookStepCompleted === 3 ? quickBookStepCompleted : 3,
            bs_import_start_date: startDate.format('YYYY-MM-DD'),
            bs_import_end_date: endDate.format('YYYY-MM-DD'),
        };

        setIsLoading(true);
        quickBookImportData(params, (message: string, resp: any) => {
            localStorage.setItem("qbo_duration", String(endDate.diff(startDate, 'months') + 1));
            trackImportJob(resp?.data?.data?.job_id);
            setIsLoading(false);
            setImportInProgress(true);
            toast.success(message);
            fetchTransactionList(false);
        }, (message: string) => {
            setIsLoading(false);
            toast.error(message);
        });
    }

    const reimportPeriods = (periods: any[]) => {
        if (!periods.length || !branchData?.id) return;
        const dates = periods.map((period: any) => moment({
            year: Number(period.year),
            month: Number(period.month) - 1,
            day: 1,
        }));
        const startDate = moment.min(dates).startOf('month');
        const endDate = moment.max(dates).endOf('month');
        const params = {
            branch_id: +branchData['id'],
            step: quickBookStepCompleted === 3 ? quickBookStepCompleted : 3,
            bs_import_start_date: startDate.format('YYYY-MM-DD'),
            bs_import_end_date: endDate.format('YYYY-MM-DD'),
        };

        setIsLoading(true);
        quickBookImportData(params, (message: string, resp: any) => {
            localStorage.setItem("qbo_duration", String(endDate.diff(startDate, 'months') + 1));
            trackImportJob(resp?.data?.data?.job_id);
            setIsLoading(false);
            setImportInProgress(true);
            setLedgerAudit(null);
            toast.success(message);
            fetchTransactionList(false);
        }, (message: string) => {
            setIsLoading(false);
            toast.error(message);
        });
    }

    const reimportSelectedOrRequiredPeriods = () => {
        const selectedPeriods = selectedPeriodsNeedingReimport();
        reimportPeriods(selectedPeriods.length > 0 ? selectedPeriods : periodsNeedingReimport());
    }

    // Poll submit job status when a submission is in progress
    useEffect(() => {
        if ((submitJobStatus !== 'in_progress') || !submitJobId) return;

        const interval = setInterval(() => {
            qbFetchJobStatus({ job_id: submitJobId }, (message: string, resp: any) => {
                const data = resp.data.data;
                setSubmitJobDescription(data.description || t('alert.submitting.processing'));

                if (data.status === 'completed') {
                    setIsLoading(false);
                    setSubmitJobStatus('completed');
                    setImportInProgress(false);
                    localStorage.removeItem("qbo_duration");
                    toast.success(t('toast.submissionCompleted'));
                    navigate(URLS.QUICKBOOK_DASHBOARD);
                } else if (data.status === 'failed') {
                    setSubmitJobStatus('failed');
                    setSubmitJobError(data.failure_message || t('toast.submissionFailed'));
                    setIsLoading(false);
                    toast.error(t('toast.submissionFailedReason', { reason: data.failure_message || t('toast.unknownError') }));
                }
            }, (message: string, resp: any) => {
                // Silently retry on polling errors
            });
        }, 5000);

        return () => clearInterval(interval);
    }, [submitJobStatus, submitJobId])

    
    function monthTransform (month:any){
        let name=moment(month,"MM").format('MMMM')
        const monthName:any={
            "January":t('month.01',{ns:'resources.form-validator'}),
            "February":t('month.02',{ns:'resources.form-validator'}),
            "March":t('month.03',{ns:'resources.form-validator'}),
            "April":t('month.04',{ns:'resources.form-validator'}),
            "May":t('month.05',{ns:'resources.form-validator'}),
            "June":t('month.06',{ns:'resources.form-validator'}),
            "July":t('month.07',{ns:'resources.form-validator'}),
            "August":t('month.08',{ns:'resources.form-validator'}),
            "September":t('month.09',{ns:'resources.form-validator'}),
            "October":t('month.10',{ns:'resources.form-validator'}),
            "November":t('month.11',{ns:'resources.form-validator'}),
            "December":t('month.12',{ns:'resources.form-validator'}),
        }
      return monthName[name] || name
  }

    const isImportActive = importInProgress || (importJobStatus === 'in_progress');

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-3 pt-3" id="ux_page_title">
                    <div className="row align-items-center">
                        <div className="col-sm-12 align-items-center d-flex">
                            <h1 className="h5 font-weight-700 mb-0 d-inline-flex">
                                {t("page.title")}
                            </h1>
                        </div>
                    </div>
                </div>

                <div className=" executive-dashboard">
                    <div className="dashboard-top-part">
                        <div className="filter-btn">
                            <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                <div className="loader"></div>
                            </div>
                        </div>
                        <Form>
                            <div className='flex justify-content-end text-end'>
                              <button type="button" className="btn btn-secondary me-2" onClick={handleBackToDashboard}>
                                  <i className="fas fa-arrow-left" aria-hidden="true"></i> {t('buttons.backToDashboard')}
                              </button>
                              <button type="button" className="btn btn-cancel" disabled={isImportActive} onClick={() => setIsOpenBBModal(true)}>{t("buttons.importMore")}</button>
                                &nbsp;
                                {
                                    ((!isImportActive && (fields.length > 0) && (searchParams['page'] == 1) && (periodsNeedingReview().length > 0)) && (
                                        <>
                                        <button type="button" className="btn btn-outline-primary" disabled={retryingAll || (submitJobStatus === 'in_progress')} onClick={retryAllPeriods}>
                                            {retryingAll ? (
                                                <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('ledgerAudit.retrying')}</>
                                            ) : selectedPeriodsNeedingReview().length > 0
                                                ? t("buttons.retrySelectedReview", { count: selectedPeriodsNeedingReview().length })
                                                : t("buttons.retryAllReview", { count: periodsNeedingReview().length })}
                                        </button>
                                        &nbsp;
                                        </>
                                    )) || <></>
                                }
                                {
                                    ((!isImportActive && (fields.length > 0) && (searchParams['page'] == 1) && (periodsNeedingReimport().length > 0)) && (
                                        <>
                                        <button type="button" className="btn btn-outline-danger" disabled={isLoading || (submitJobStatus === 'in_progress')} onClick={reimportSelectedOrRequiredPeriods}>
                                            {isLoading ? (
                                                <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('buttons.reimporting')}</>
                                            ) : selectedPeriodsNeedingReimport().length > 0
                                                ? t("buttons.reimportSelectedPeriods", { count: selectedPeriodsNeedingReimport().length })
                                                : t("buttons.reimportRequiredPeriods", { count: periodsNeedingReimport().length })}
                                        </button>
                                        &nbsp;
                                        </>
                                    )) || <></>
                                }
                                {
                                    ((!isImportActive && (fields.length > 0) && (searchParams['page'] == 1)) && (
                                        <button type="button" className="btn btn-primary" disabled={submitJobStatus === 'in_progress'} onClick={handleSubmit(processData)}>
                                            {submitJobStatus === 'in_progress' ? (
                                                <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('buttons.submitting')}</>
                                            ) : t("buttons.verifyAndSubmit")}
                                        </button>
                                    )) || <></>
                                }
                                {
                                    ((!isImportActive && verificationOverride && (submitJobStatus !== 'in_progress')) && (
                                        <>
                                            &nbsp;
                                            <button type="button" className="btn btn-outline-danger" disabled={isLoading}
                                                onClick={() => { if (window.confirm(t('buttons.submitAnywayConfirm'))) submitAnyway(); }}>
                                                {t('buttons.submitAnyway')}
                                            </button>
                                        </>
                                    )) || <></>
                                }
                            </div>


                            <br></br>
                            {/* <div className="table-responsive list-table table-scroll" style={{ maxHeight: contentMaxHeight }}> */}
                            {((importJobStatus === 'partial_failed') || (importJobStatus === 'failed')) && (
                                <Alert variant={importJobStatus === 'failed' ? 'danger' : 'warning'}>
                                    <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>&nbsp;&nbsp;
                                    <strong>{t('alert.importFailed.title')} </strong>
                                    {importJobDescription || t('alert.importFailed.desc')}&nbsp;
                                    {(failedImportMonths.length > 0) && (
                                        <>
                                            <span>{t('alert.importFailed.months', { months: failedImportMonths.join(', ') })}</span>&nbsp;&nbsp;
                                            <button type="button" className="btn btn-sm btn-outline-primary" disabled={isLoading} onClick={retryFailedImportMonths}>
                                                <i className="fas fa-redo" aria-hidden="true"></i> {t('buttons.retryFailedMonths')}
                                            </button>
                                        </>
                                    )}
                                </Alert>
                            )}
                            {(() => {
                                const totalExpected = Math.max(
                                    parseInt(localStorage.getItem("qbo_duration") || "0"),
                                    parseImportTotalMonths(importJobDescription),
                                );
                                if ((importJobStatus === 'partial_failed') || (importJobStatus === 'failed')) {
                                    return null;
                                }
                                const hasIncompleteRecords = records.data.some((r: any) => r.status === 1);
                                const completedRecords = totalExpected === 1
                                    ? (hasIncompleteRecords ? 0 : 1)
                                    : records.data.filter((r: any) => r.status === 2).length + totalRemovedRecord;
                                const progressCompleted = Math.min(completedRecords, totalExpected);
                                const progressTotal = Math.max(completedRecords, totalExpected);
                                
                                if (hasIncompleteRecords || isImportActive) {
                                    return (
                                        <Alert variant={'info'}>
                                            <i className="fa fa-spinner fa-spin fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                                            {t("pending.download.message", { 
                                                total_records: progressCompleted, 
                                                qbo_duration: progressTotal 
                                            })}
                                            {importJobDescription && (
                                                <div className="mt-2">{t('pending.download.progress', { progress: importJobDescription })}</div>
                                            )}
                                        </Alert>
                                    );
                                } else if (records.total_records === 0) {
                                    return (
                                        <Alert variant={'warning'}>
                                            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>&nbsp;&nbsp;
                                            {t("table.noData")}
                                        </Alert>
                                    );
                                } else {
                                    return (
                                        <Alert variant={'success'}>
                                            <i className="fas fa-check-circle" aria-hidden="true"></i>&nbsp;&nbsp;
                                            {t("completed.download.message")}
                                        </Alert>
                                    );
                                }
                            })()}

                            {!isImportActive && records.data.some((r: any) => r.status === 3) && (
                                <Alert variant={'warning'}>
                                    <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>&nbsp;&nbsp;
                                    <strong>{t('alert.reimportRequired.title')}</strong>
                                    <p className="mb-0 mt-2" style={{ fontSize: '0.85rem' }}>
                                        {t('alert.reimportRequired.desc')}
                                    </p>
                                    <button type="button" className="btn btn-sm btn-outline-danger mt-2" disabled={isLoading} onClick={reimportSelectedOrRequiredPeriods}>
                                        {selectedPeriodsNeedingReimport().length > 0
                                            ? t("buttons.reimportSelectedPeriods", { count: selectedPeriodsNeedingReimport().length })
                                            : t("buttons.reimportRequiredPeriods", { count: periodsNeedingReimport().length })}
                                    </button>
                                </Alert>
                            )}
                            
                            {(submitJobStatus === 'in_progress') && (
                                <Alert variant={'primary'}>
                                    <i className="fa fa-spinner fa-spin fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                                    <strong>{t('alert.submitting.title')} </strong>{submitJobDescription}
                                </Alert>
                            )}
                            {(submitJobStatus === 'failed') && (
                                <Alert variant={'danger'}>
                                    <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>&nbsp;&nbsp;
                                    <strong>{t('alert.failed.title')} </strong>{submitJobError}&nbsp;&nbsp;
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={retrySubmit}>
                                        <i className="fas fa-redo" aria-hidden="true"></i> {t('button.retry')}
                                    </button>
                                </Alert>
                            )}
                            {ledgerAudit && (() => {
                                const ledgerHasBlockers = hasLedgerBlockers(ledgerAudit);
                                const verificationHasBlockers = hasVerificationBlockers(ledgerAudit);
                                const areas = ledgerAudit.verification?.areas || {};
                                // BS/PNL statement totals never block (report-engine computation, not a
                                // real "missed data" signal) — only ledger/register coverage does.
                                const registerBlocked = Number(areas.REGISTER?.blocker_count || 0) > 0;
                                const unmappedCount = Number(ledgerAudit.status_counts?.unmapped || 0) + Number(ledgerAudit.status_counts?.invalid || 0);
                                const verificationDetails = ledgerAudit.verification?.details || [];
                                const blockerDetails = verificationDetails.filter((row: any) => row.status === 'blocked');
                                const visibleVerificationDetails = blockerDetails.length > 0 ? blockerDetails : verificationDetails;
                                return (
                                <Alert variant={(ledgerHasBlockers || verificationHasBlockers) ? 'warning' : 'success'}>
                                    <div className="d-flex justify-content-between gap-3 flex-wrap">
                                        <div className="flex-grow-1">
                                            <strong>
                                                {t('ledgerAudit.title', {
                                                    month: monthTransform(ledgerAudit.period?.month),
                                                    year: ledgerAudit.period?.year
                                                })}
                                            </strong>
                                            {(!ledgerHasBlockers && !verificationHasBlockers) ? (
                                                <p className="mb-0 mt-2" style={{ fontSize: '0.9rem' }}>
                                                    <i className="fas fa-check-circle text-success" aria-hidden="true"></i>{' '}
                                                    {t('ledgerAudit.readyToSubmit')}
                                                </p>
                                            ) : (
                                                <ul className="mb-0 mt-2 ps-3" style={{ fontSize: '0.9rem' }}>
                                                    {ledgerHasBlockers && (
                                                        <li>{t('ledgerAudit.needsMapping', { count: unmappedCount })}</li>
                                                    )}
                                                    {registerBlocked && !ledgerHasBlockers && (
                                                        <li>{t('ledgerAudit.registerNeedsReview')}</li>
                                                    )}
                                                </ul>
                                            )}
                                        </div>
                                        <div className="d-flex align-items-start gap-2">
                                            {ledgerHasBlockers && (
                                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => navigate(URLS.QUICKBOOK_COA)}>
                                                    {t('ledgerAudit.openMappings')}
                                                </button>
                                            )}
                                            {(ledgerHasBlockers || verificationHasBlockers) && (
                                                <button type="button" className="btn btn-sm btn-primary" disabled={ledgerRetrying || retryingAll} onClick={retrySelectedOrCurrentPeriod}>
                                                    {(ledgerRetrying || retryingAll) ? t('ledgerAudit.retrying') : selectedPeriodsNeedingReview().length > 0
                                                        ? t('buttons.retrySelectedReview', { count: selectedPeriodsNeedingReview().length })
                                                        : t('ledgerAudit.retryPeriod')}
                                                </button>
                                            )}
                                            <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => { setShowLedgerDetails(false); setLedgerAudit(null); }}>
                                                {t('ledgerAudit.close')}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mt-3">
                                        <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setShowLedgerDetails((v: boolean) => !v)}>
                                            {showLedgerDetails ? t('ledgerAudit.hideTechnical') : t('ledgerAudit.showTechnical')}
                                        </button>
                                    </div>

                                    {showLedgerDetails && (<>
                                    {(ledgerHasBlockers || verificationHasBlockers) && (
                                        <div className="d-flex gap-2 mt-2">
                                            <button type="button" className="btn btn-sm btn-outline-primary" disabled={ledgerMappingSaving} onClick={saveLedgerOverrides}>
                                                {ledgerMappingSaving ? t('ledgerAudit.savingOverrides') : t('ledgerAudit.saveOverrides')}
                                            </button>
                                        </div>
                                    )}

                                    <table className="table table-sm table-bordered mt-3 mb-0 bg-white" style={{ fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr>
                                                <th>{t('ledgerAudit.category')}</th>
                                                {ledgerStatusKeys(ledgerAudit).map((key: any) => (
                                                    <th className="text-end" key={key}>{formatLedgerStatus(key)}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>{t('ledgerAudit.lineStatus')}</td>
                                                {ledgerStatusKeys(ledgerAudit).map((key: any) => (
                                                    <td className="text-end" key={key}>{ledgerAudit.status_counts?.[key] ?? '-'}</td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{t('ledgerAudit.transactionStatus')}</td>
                                                {ledgerStatusKeys(ledgerAudit).map((key: any) => (
                                                    <td className="text-end" key={key}>{ledgerAudit.transaction_status_counts?.[key] ?? '-'}</td>
                                                ))}
                                            </tr>
                                        </tbody>
                                    </table>

                                    {ledgerAudit.verification && (
                                        <table className="table table-sm table-bordered mt-3 mb-2 bg-white" style={{ fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr>
                                                    <th>{t('verification.area')}</th>
                                                    <th>{t('verification.status')}</th>
                                                    <th className="text-end">{t('verification.blockers')}</th>
                                                    <th className="text-end">{t('verification.difference')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.keys(ledgerAudit.verification.areas || {}).map((area: string) => (
                                                    <tr key={area}>
                                                        <td>{areaLabel(area)}</td>
                                                        <td>{formatLedgerStatus(ledgerAudit.verification.areas[area]?.status || 'passed')}</td>
                                                        <td className="text-end">{ledgerAudit.verification.areas[area]?.blocker_count || 0}</td>
                                                        <td className="text-end">{formatMoney(ledgerAudit.verification.areas[area]?.difference || 0)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    {(visibleVerificationDetails.length > 0) && (
                                        <table className="table table-sm table-bordered mt-3 mb-2" style={{ fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr>
                                                    <th>{t('verification.area')}</th>
                                                    <th>{t('ledgerAudit.accountId')}</th>
                                                    <th className="text-end">{t('verification.qboTotal')}</th>
                                                    <th className="text-end">{t('verification.revrexTotal')}</th>
                                                    <th className="text-end">{t('ledgerAudit.difference')}</th>
                                                    <th>{t('ledgerAudit.message')}</th>
                                                    <th>{t('ledgerAudit.overrideMapping')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleVerificationDetails.map((row: any, i: number) => {
                                                    const canMoveRow = canMoveRegisterRow(row);
                                                    const candidates = canMoveRow ? registerMoveCandidates(row) : [];
                                                    const moveOptions = candidates.length > 0
                                                        ? candidates.map((candidate: any) => verificationAccountOption(candidate))
                                                        : glAccountOptions.filter((account: any) => Number(account.value) !== Number(row.revrex_account_id));
                                                    const txnId = registerTxnId(row);
                                                    const selectedMove = registerOverrideEdits[registerOverrideKey(txnId, row.revrex_account_id)] || '';
                                                    return (
                                                        <tr key={`${row.area}-${txnId}-${row.revrex_account_id}-${i}`}>
                                                            <td>{areaLabel(row.area)}</td>
                                                            <td>{verificationAccountLabel(row)}</td>
                                                            <td className="text-end">{formatMoney(row.qbo_total)}</td>
                                                            <td className="text-end">{formatMoney(row.revrex_total)}</td>
                                                            <td className="text-end text-danger">{formatMoney(row.difference)}</td>
                                                            <td>{row.message || '-'}</td>
                                                            <td>
                                                                {canMoveRow ? (
                                                                    <>
                                                                        <select
                                                                            className="form-select form-select-sm"
                                                                            value={selectedMove}
                                                                            onChange={(event: any) => handleRegisterOverrideChange(row, event.target.value)}
                                                                        >
                                                                            <option value="">
                                                                                {candidates.length > 0 ? t('ledgerAudit.selectMoveTarget') : t('ledgerAudit.selectAnyMoveTarget')}
                                                                            </option>
                                                                            {moveOptions.map((account: any) => (
                                                                                <option key={account.value} value={account.value}>{account.label}</option>
                                                                            ))}
                                                                        </select>
                                                                    </>
                                                                ) : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}

                                    {!ledgerHasBlockers && !verificationHasBlockers && ((ledgerAudit.register_comparison?.items || []).length > 0) && (
                                        <p className="mb-0 mt-3" style={{ fontSize: '0.85rem' }}>
                                            {t('ledgerAudit.diagnosticHidden')}
                                        </p>
                                    )}

                                    {ledgerHasBlockers && ((ledgerAudit.register_comparison?.items || []).length > 0) && (
                                        <table className="table table-sm table-bordered mt-3 mb-2" style={{ fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr>
                                                    <th>{t('ledgerAudit.accountId')}</th>
                                                    <th className="text-end">{t('ledgerAudit.qboActivity')}</th>
                                                    <th className="text-end">{t('ledgerAudit.registerTotal')}</th>
                                                    <th className="text-end">{t('ledgerAudit.difference')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ledgerAudit.register_comparison.items.map((row: any) => (
                                                    <tr key={row.gl_mapping_account_id}>
                                                        <td>{row.gl_mapping_account_id || t('table.na')}</td>
                                                        <td className="text-end">{formatMoney(row.ledger_activity_total)}</td>
                                                        <td className="text-end">{formatMoney(row.register_total)}</td>
                                                        <td className="text-end text-danger">{formatMoney(row.difference)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    {((ledgerAudit.exceptions?.items || []).length > 0) && (
                                        <table className="table table-sm table-bordered mt-3 mb-0" style={{ fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr>
                                                    <th>{t('ledgerAudit.date')}</th>
                                                    <th>{t('ledgerAudit.type')}</th>
                                                    <th>{t('ledgerAudit.qboAccount')}</th>
                                                    <th>{t('ledgerAudit.defaultMapping')}</th>
                                                    <th>{t('ledgerAudit.overrideMapping')}</th>
                                                    <th>{t('ledgerAudit.message')}</th>
                                                    <th className="text-end">{t('ledgerAudit.amount')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ledgerAudit.exceptions.items.map((row: any) => {
                                                    const selectedOverride = Object.prototype.hasOwnProperty.call(ledgerOverrideEdits, row.id)
                                                        ? ledgerOverrideEdits[row.id]
                                                        : (row.override_gl_mapping_account_id || '');
                                                    return (
                                                        <tr key={row.id}>
                                                            <td>{row.transaction_at || '-'}</td>
                                                            <td>{row.qbo_transaction_type || '-'}</td>
                                                            <td>
                                                                {row.qbo_account_name || row.qbo_account_id || t('table.na')}
                                                                {row.qbo_split_account_name && <div className="text-muted">{t('ledgerAudit.split')}: {row.qbo_split_account_name}</div>}
                                                            </td>
                                                            <td>{glAccountLabel(row.gl_mapping_account_id)}</td>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={selectedOverride}
                                                                    onChange={(event: any) => handleLedgerOverrideChange(row.id, event.target.value)}
                                                                >
                                                                    <option value="">{t('ledgerAudit.useDefaultMapping')}</option>
                                                                    {glAccountOptions.map((account: any) => (
                                                                        <option key={account.value} value={account.value}>{account.label}</option>
                                                                    ))}
                                                                </select>
                                                                {(row.mapping_source === 'manual_override') && (
                                                                    <div className="text-muted">{t('ledgerAudit.manualOverrideApplied')}</div>
                                                                )}
                                                            </td>
                                                            <td>{row.validation_message || row.transaction_validation_message || row.validation_status || '-'}</td>
                                                            <td className="text-end">{formatMoney(row.amount)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                    </>)}
                                </Alert>
                                );
                            })()}

                            <div className="table-responsive list-table table-scroll">
                                <table className="table table-default">
                                    <thead>
                                        <tr className="align-middle">
                                            <th className="w-5 text-start">
                                                <div className="form-check ">
                                                    <label >
                                                        {/* <input ref={checkAllRef} className="form-check-input wh-20 checkAll" type="checkbox" disabled={fields.length > 0 && fields[0].id ? true : false} onChange={handleCheckAllChange} /> */}
                                                        <input ref={checkAllRef} className="form-check-input wh-20 checkAll" type="checkbox" onChange={handleCheckAllChange} />
                                                    </label>
                                                </div>
                                            </th>
                                            <th className="text-center w-5">{t("table.headers.month")}</th>
                                            <th className="text-center w-5">{t("table.headers.year")}</th>
                                            <th className="text-center w-5">{t("table.headers.status")}</th>
                                            <th className="text-center w-5">{t("table.headers.action")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            fields.length > 0 ?
                                                fields.map((data: any, index: any) => (
                                                    <tr className={`align-middle ${rowClassName(fields[index])}`} key={data['field_id']}>
                                                        <td className="text-start">
                                                            <div className="form-check ">
                                                                <label >
                                                                    <input className="form-check-input wh-20 checkbox" {...register(`BS_PL.${index}.checked`)}
                                                                        name={`BS_PL.${index}.checked`}
                                                                        defaultChecked={fields[index]['checked']}
                                                                        type="checkbox"
                                                                        disabled={!isSelectablePeriod(fields[index])}
                                                                        onChange={(e: any) => handleCheckAll(e, index)} />
                                                                </label>
                                                            </div>
                                                        </td>
                                                        <td className={"text-center"}>
                                                            <strong>{monthTransform(fields[index].month)}</strong>
                                                        </td>
                                                        <td className={"text-center"}>
                                                            <strong>{fields[index].year}</strong>
                                                        </td>
                                                        <td className={"text-center"}>
                                                            <strong>
                                                                {verificationBlocked(fields[index]) ? (
                                                                    <span className="text-danger">
                                                                        <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {t('status.verificationRequired')}
                                                                    </span>
                                                                ) : ledgerBlocked(fields[index]) ? (
                                                                    <span className="text-warning">
                                                                        <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {t('status.reviewRequired')}
                                                                    </span>
                                                                ) : fields[index].status == 1 ? (
                                                                    <span className="text-warning">
                                                                        <i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('status.pending')}
                                                                    </span>
                                                                ) : fields[index].status == 3 ? (
                                                                    <span className="text-danger">
                                                                        <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {t('status.reimportRequired')}
                                                                    </span>
                                                                ) : (
                                                    <span className="text-success">
                                                                        <i className="fas fa-check-circle" aria-hidden="true"></i> {t('status.completed')}
                                                                    </span>
                                                                )}
                                                            </strong>
                                                        </td>
                                                        <td className={"text-end"}>
                                                            {
                                                                (verificationBlocked(fields[index]) || ledgerBlocked(fields[index]) || (fields[index].status == 3)) && (
                                                                    <>
                                                                        <button type='button' className='btn btn-outline-info btn-sm me-1' disabled={ledgerAuditLoading} onClick={() => fetchLedgerAudit(fields[index])}>
                                                                            {ledgerAuditLoading ? t('ledgerAudit.loading') : t("table.actions.reviewLedger")}
                                                                        </button>&nbsp;
                                                                    </>
                                                                )
                                                            }
                                                            {
                                                                fields[index].status == 3 ? (
                                                                    <>
                                                                        <button type='button' className='btn btn-danger btn-sm' disabled={isLoading} onClick={() => reimportPeriods([fields[index]])}>{t("table.actions.reimportPeriod")}</button>
                                                                    </>
                                                                ) : (<></>)
                                                            }
                                                            {
                                                                fields[index].status == 2 ? (
                                                                    <>
                                                                        <button type='button' className='btn btn-warning btn-sm' onClick={() => navigate('/quickbook-bs/' + fields[index].month + '/' + fields[index].year)}>{t("table.actions.viewBS")}</button>&nbsp;
                                                                        <button type='button' className='btn btn btn-success btn-sm' onClick={() => navigate('/quickbook-pl/' + fields[index].month + '/' + fields[index].year)}>{t("table.actions.viewPL")}</button>
                                                                    </>
                                                                ) : (<></>)
                                                            }

                                                        </td>
                                                    </tr>
                                                )) : <tr>
                                                    <td colSpan={5} className='text-center'>{t("table.noData")}</td>
                                                </tr>
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </Form>
                        {(fields.length === 0) && (
                            <div className="d-flex justify-content-center mt-4 pb-4">
                                <button type="button" className="btn btn-primary px-5 py-2" onClick={() => navigate(URLS.QUICKBOOK_DASHBOARD)}>
                                    {t('buttons.continue')}
                                </button>
                            </div>
                        )}
                    </div>
                    {/* <div className='d-flex justify-content-center'>
                        <Pagination count={Math.ceil(records['total_records'] / searchParams['per_page'])} showFirstButton showLastButton onChange={onChangePage} />
                    </div> */}

                </div>
            </div>

            {
                isOpenBBModal &&
                <BeginningBalanceImportModal step={quickBookStepCompleted === 3 ? quickBookStepCompleted: 3} selectDate={setSelectedDateBB} handleBBDateSelection={_handleBBDateSelection} closeModal={() => setIsOpenBBModal(false)} />
            }

            {
                showDashboardNavConfirmModal && (
                    <Modal show={true} onHide={() => setShowDashboardNavConfirmModal(false)} backdrop="static" animation={false}>
                        <Modal.Header closeButton>
                            <Modal.Title className="h5 font-weight-700">{t('navConfirm.title')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="d-flex align-items-start gap-3">
                                <i className="fas fa-info-circle text-info fa-2x mt-1" aria-hidden="true"></i>
                                <div>
                                    <p className="mb-2 font-weight-600 text-dark">
                                        {t('navConfirm.message')}
                                    </p>
                                    <p className="text-muted small mb-0">
                                        {t('navConfirm.detail')}
                                    </p>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" className="btn btn-secondary" onClick={() => setShowDashboardNavConfirmModal(false)}>
                                {t('navConfirm.stay')}
                            </button>
                            <button type="button" className="btn btn-primary" onClick={() => { setShowDashboardNavConfirmModal(false); navigate(URLS.QUICKBOOK_DASHBOARD); }}>
                                {t('navConfirm.proceed')}
                            </button>
                        </Modal.Footer>
                    </Modal>
                )
            }
        </div>

    )
}

export default QBBalanceSheetPLList;
